import { useEffect, useRef } from "react";

/**
 * Floating callouts anchored to the live waveform.
 *
 * Each callout owns a horizontal LANE and tracks the strongest crest inside
 * it. Lanes never overlap, so the three cards can never collide with each
 * other however the wave drifts — which is what makes "chase the peak" safe
 * here. Within its lane the marker sits on the crest of the painted strand
 * bundle (engine.sampleLane reads last frame's stroked geometry), so the dot
 * touches the wave at every size and through every resize instead of hovering
 * over it — no fixed pixel coordinates are involved anywhere.
 *
 * Nothing here goes through React state: positions are written straight to
 * refs inside one rAF loop, so the page never re-renders while it animates.
 * The percentages are read off that same crest height and rewritten at ~5Hz —
 * fast enough to feel live, slow enough to read.
 */

const BLUE = "#60a5fa";
const PURPLE = "#a78bfa";

/**
 * Lanes are deliberately narrow and widely separated. Measured over 12
 * simulated minutes, wide lanes let adjacent crests drift within 40px of each
 * other — closer than one card is wide, so the cards would collide. At this
 * spacing the nearest two anchors stay ~0.18 x width apart, which exceeds the
 * widest card at every size the callouts are shown — the cards shrink on phones
 * so this still holds there — so no collision handling is needed. Connectors are
 * always truly vertical: the line hangs from the dot, and only the card slides
 * sideways when an edge clamp applies.
 */
const CALLOUTS = [
  { key: "engagement", label: "Engagement", color: BLUE, lane: [0.10, 0.24] },
  { key: "focus", label: "Focus", color: PURPLE, lane: [0.42, 0.58] },
  { key: "wellbeing", label: "Wellbeing", color: BLUE, lane: [0.76, 0.90] },
];

// Vertical gap between the bottom of a card and its dot. On a phone the panel
// is short enough that the full gap would push every card into the top clamp,
// which flattens all three onto one line and hides which dot each belongs to.
const CARD_GAP = 44;
const CARD_GAP_NARROW = 22;
const NARROW_W = 700;
const EDGE_PAD = 8;

/**
 * Percentages are a linear read of crest height, identical for all three
 * lanes: no per-callout base, so whichever lane holds the tallest peak at any
 * moment shows the largest number. Range is chosen so a calm lane still reads
 * as a real figure rather than 0%.
 */
const PCT_MIN = 3;
const PCT_SPAN = 15;
// Exponential smoothing per frame — the crest argmax can jump when two energy
// bumps swap dominance inside a lane; this keeps the marker gliding.
const SMOOTHING = 0.08;
const VALUE_INTERVAL_MS = 200;

export default function WaveCallouts({ engine }) {
  const rootRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    const root = rootRef.current;
    if (!engine || !root) return undefined;

    const items = itemRefs.current.filter(Boolean);
    if (items.length !== CALLOUTS.length) return undefined;

    // Card height is needed to know where the connector starts. Measuring it
    // per frame would force a layout every frame, so measure on mount and on
    // resize only.
    // Card width matters too: on a narrow panel a crest near either end would
    // otherwise carry its card off the edge, so the card is clamped back inside
    // while the dot stays on the wave.
    let cardHeights = [];
    let cardWidths = [];
    const measure = () => {
      cardHeights = items.map((it) => it.card.offsetHeight || 44);
      cardWidths = items.map((it) => it.card.offsetWidth || 70);
    };
    measure();

    const state = CALLOUTS.map(() => ({ x: null, y: 0, energy: 0 }));
    let lastValueAt = 0;
    let lastShown = CALLOUTS.map(() => null);
    let rafId = null;

    const frame = (now) => {
      const h = engine.height;
      const w = engine.width;

      if (w > 0 && h > 0) {
        const writeValues = now - lastValueAt >= VALUE_INTERVAL_MS;
        if (writeValues) lastValueAt = now;

        for (let i = 0; i < CALLOUTS.length; i++) {
          const cfg = CALLOUTS[i];
          const sample = engine.sampleLane(cfg.lane[0], cfg.lane[1]);
          if (!sample) continue;

          const st = state[i];
          // Only x is eased. Easing y as well would put the marker at a point
          // that is on neither the old crest nor the new one — a smoothed
          // coordinate pair does not describe a point on the curve — so y is
          // resolved from the wave at whatever x the marker currently holds.
          // First sample snaps into place; afterwards it eases.
          if (st.x === null) {
            st.x = sample.x;
            st.energy = sample.energy;
          } else {
            st.x += (sample.x - st.x) * SMOOTHING;
            st.energy += (sample.energy - st.energy) * SMOOTHING;
          }
          const yOnWave = engine.topAt(st.x);
          st.y = yOnWave == null ? sample.y : yOnWave;

          const it = items[i];
          const cardH = cardHeights[i] || 44;
          const cardW = cardWidths[i] || 70;
          const gap = w <= NARROW_W ? CARD_GAP_NARROW : CARD_GAP;

          // The card rides above its own dot, so the connector can never
          // invert when a crest climbs past a fixed card position — which the
          // measured crest range (y as high as 40px) says would happen.
          const cardY = Math.max(EDGE_PAD, Math.min(h - cardH - EDGE_PAD, st.y - gap - cardH));
          const lineTop = cardY + cardH;

          // Centred on the dot, then pulled back inside the panel if that would
          // hang it over an edge. The wrap already sits at st.x, so the card's
          // own offset is expressed relative to it.
          const half = cardW / 2;
          const clampedCenter = cardW + 2 * EDGE_PAD >= w
            ? w / 2
            : Math.max(EDGE_PAD + half, Math.min(w - EDGE_PAD - half, st.x));
          const cardDX = clampedCenter - st.x - half;

          it.wrap.style.transform = `translate3d(${st.x.toFixed(1)}px,0,0)`;
          it.card.style.transform = `translate3d(${cardDX.toFixed(1)}px,${cardY.toFixed(1)}px,0)`;
          it.dot.style.transform = `translate3d(0,${st.y.toFixed(1)}px,0) translate(-50%,-50%)`;
          it.line.style.top = `${lineTop.toFixed(1)}px`;
          it.line.style.height = `${Math.max(0, st.y - lineTop).toFixed(1)}px`;

          if (writeValues) {
            const pct = Math.round(PCT_MIN + PCT_SPAN * st.energy);
            if (pct !== lastShown[i]) {
              lastShown[i] = pct;
              it.value.textContent = `↑ ${pct}%`;
            }
          }
        }

        if (root.style.opacity !== "1") root.style.opacity = "1";
      }

      rafId = requestAnimationFrame(frame);
    };

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduced.matches) {
      // Engine paints one static frame; place the callouts on it once.
      frame(performance.now());
      cancelAnimationFrame(rafId);
      rafId = null;
    } else {
      rafId = requestAnimationFrame(frame);
    }

    const ro = new ResizeObserver(measure);
    ro.observe(root);

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [engine]);

  return (
    <div className="wave-callouts" ref={rootRef} aria-hidden="true">
      {CALLOUTS.map((c, i) => (
        <div
          className="wc-anchor"
          key={c.key}
          ref={(el) => {
            if (!el) { itemRefs.current[i] = null; return; }
            itemRefs.current[i] = {
              wrap: el,
              card: el.querySelector(".wc-card"),
              line: el.querySelector(".wc-line"),
              dot: el.querySelector(".wc-dot"),
              value: el.querySelector(".wc-value"),
            };
          }}
        >
          <div className="wc-card">
            <div className="wc-label">
              <span className="wc-swatch" style={{ background: c.color }} />
              {c.label}
            </div>
            <div className="wc-value" style={{ color: c.color }}>
              {"↑ —"}
            </div>
          </div>
          <div className="wc-line" style={{ background: `linear-gradient(180deg, ${c.color}00, ${c.color}88)` }} />
          <div className="wc-dot" style={{ background: c.color, boxShadow: `0 0 0 3px ${c.color}33, 0 0 10px ${c.color}` }} />
        </div>
      ))}
    </div>
  );
}
